import React from 'react'
import { getRowValue } from 'components/spreadsheet/helpers/functions'
import {
  IColumnValuesCount,
  IGroupCollapsed,
  ITreeGroupRow,
  IViewPortColumn,
  rowHeights
} from 'components/spreadsheet/types'
import GroupCell from 'components/spreadsheet/components/grouping/GroupCell'
import RowNumberCell from 'components/spreadsheet/components/addRow/RowNumberCell'
import Aggregate from 'components/spreadsheet/components/aggregate'
import { getCellColour, getRowLevelColour } from 'components/spreadsheet/helpers/colouring'
import { Triangle } from 'components/icons'
import { ITableRow, ITableViewColour, ITableViewColumn, IContextMenuState } from 'types'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { COLUMN_MIN_WIDTH, ROW_NUMBER_COLUMN_WIDTH, SMALL_ROW_HEIGHT } from 'style/constants'
import { INDENT_PX } from 'components/spreadsheet/constants/const'
import { useThemedColours } from 'hooks/useTheme'

interface Props {
  rowNumber: number
  colourSettings: ITableViewColour[]
  collapsedRow?: IGroupCollapsed
  treeGroupRow?: ITreeGroupRow
  treeColumnWidth?: number
  onGroupToggle: (groupKey: string) => void
  onGroupContext: (menu: IContextMenuState) => void
  rows: ITableRow[]
  loading: boolean
  groupedColumns: IViewPortColumn[]
  nonGroupedColumns: IViewPortColumn[]
  columnValuesCount: IColumnValuesCount
  groupRowSpans?: Map<string, number>
  rowIndex?: number
  addRowOffset?: number
  /** The first row under the header, whose line the header's own border already draws. */
  isFirstRow?: boolean
}

const CollapsedRow: React.FC<Props> = ({
  rowNumber,
  colourSettings,
  collapsedRow,
  treeGroupRow,
  treeColumnWidth = 0,
  onGroupToggle,
  onGroupContext,
  rows,
  loading,
  groupedColumns,
  nonGroupedColumns,
  columnValuesCount,
  groupRowSpans,
  rowIndex,
  addRowOffset = 0,
  isFirstRow = false
}) => {
  const themed = useThemedColours()
  const { spreadsheetData, selectedColumnIds, selectedGroupCell, selectGroupCell } = useDataContext()
  const isTreeMode = !!treeGroupRow
  const rowHeightPx = SMALL_ROW_HEIGHT
  // Every row draws only its top line, so a boundary is one line wherever it is; the first
  // row leaves it to the header's border, as data rows do, rather than doubling it.
  const topLine = isFirstRow ? '' : 'border-t border-t-divider '

  // --- Tree mode: single indented cell with chevron + label ---
  let treeCell: React.ReactNode = null
  let treeRowBgColour: string | null = null
  let treeRowFontColour: string | undefined
  let treeRowFontBold = false
  let treeRowFontItalic = false
  let treeRowFontUnderline = false

  if (isTreeMode && treeGroupRow) {
    const indentPx = treeGroupRow.depth * INDENT_PX

    // Compute colouring for this group's column and value.
    // Step 1: Evaluate row-level (applyToEntireRow) rules against the first child row.
    // Step 2: Pass the result to getCellColour so cell-specific rules on the grouped column also apply.
    const firstChildRow = treeGroupRow.childRows[0] ?? null
    const groupValue = firstChildRow
      ? getRowValue(
          firstChildRow,
          treeGroupRow.groupByColumn.publicId,
          spreadsheetData.columnIdToIndex,
          spreadsheetData.columnStore
        )
      : treeGroupRow.label
    const columnLookup = new Map(spreadsheetData.viewDetails.columns.map((col) => [col.publicId, col]))
    // In tree mode, evaluate colour rules that target THIS group's column specifically.
    // Filter to only include rules whose columnId matches this group's column — this prevents
    // a rule on Level 1 from colouring Level 2/3 groups (since the firstChildRow has values
    // for all grouping columns). We also treat these rules as row-level so they always apply
    // to the group bar regardless of the applyToEntireRow toggle.
    const groupColumnId = treeGroupRow.groupByColumn.publicId
    const treeColourSettings = colourSettings.filter((cs) => cs.columnId === groupColumnId)
    const rowLevelColour = firstChildRow
      ? getRowLevelColour(
          treeColourSettings.map((cs) => ({ ...cs, applyToEntireRow: true })),
          firstChildRow,
          columnLookup,
          spreadsheetData.columnIdToIndex,
          columnValuesCount,
          true, // grey background as default
          undefined,
          spreadsheetData.columnStore
        )
      : null
    const treeCellColour = getCellColour(
      treeGroupRow.groupByColumn as ITableViewColumn,
      columnValuesCount,
      firstChildRow,
      groupValue,
      treeColourSettings,
      true, // grey background as default
      undefined,
      rowLevelColour
    )

    // If a colour rule matched, propagate the background to the aggregate cells
    const defaultBg = 'var(--color-surface-sunken)'
    if (treeCellColour.backgroundColour && treeCellColour.backgroundColour !== defaultBg) {
      treeRowBgColour = treeCellColour.backgroundColour
      treeRowFontColour = treeCellColour.fontColour
    }
    treeRowFontBold = treeCellColour.fontBold ?? false
    treeRowFontItalic = treeCellColour.fontItalic ?? false
    treeRowFontUnderline = treeCellColour.fontUnderline ?? false

    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation()
      onGroupToggle(treeGroupRow.groupKey)
    }

    const handleSelect = (e: React.MouseEvent) => {
      if (e.button !== 0 || !firstChildRow) return
      selectGroupCell({ columnId: groupColumnId, groupKey: treeGroupRow.groupKey, rowId: firstChildRow.publicId })
    }

    const handleContextMenu = (e: React.MouseEvent) => {
      e.preventDefault()
      onGroupContext({
        open: true,
        top: `${e.clientY + 5}px`,
        left: `${e.clientX - 120}px`,
        right: 'auto',
        bottom: 'auto',
        columnId: groupColumnId,
        groupKey: treeGroupRow.groupKey,
        rowId: firstChildRow?.publicId
      })
    }

    const isTreeCellSelected =
      selectedGroupCell?.columnId === groupColumnId && selectedGroupCell?.groupKey === treeGroupRow.groupKey

    treeCell = (
      <div
        className={`sticky z-[3] flex items-center ${topLine}border-r border-r-divider cursor-pointer select-none${isTreeCellSelected ? ' outline-1 -outline-offset-1 outline-blue' : ''}${treeCellColour.fontBold ? ' font-bold' : ''}${treeCellColour.fontItalic ? ' italic' : ''}${treeCellColour.fontUnderline ? ' underline' : ''}`}
        style={{
          width: `${treeColumnWidth}px`,
          height: `${rowHeightPx}px`,
          left: `${ROW_NUMBER_COLUMN_WIDTH}px`,
          paddingLeft: `${indentPx + 8}px`,
          backgroundColor: themed.fill(treeCellColour.backgroundColour),
          color: themed.text(treeCellColour.fontColour, treeCellColour.backgroundColour)
        }}
        data-testid="tree-group-header"
        data-label={treeGroupRow.label}
        data-depth={treeGroupRow.depth}
        data-expanded={treeGroupRow.isExpanded}
        onMouseDown={handleSelect}
        onClick={handleToggle}
        onContextMenu={handleContextMenu}
      >
        <span className="mr-1.5 flex-shrink-0 text-muted">
          <Triangle className="w-2.5 h-2.5" type={treeGroupRow.isExpanded ? 'down' : 'right'} />
        </span>
        <span className={`truncate${treeCellColour.fontBold ? ' font-bold' : ' font-medium'}`}>
          {treeGroupRow.label}
        </span>
      </div>
    )
  }

  // --- Standard mode: multiple GroupCell elements ---
  let groupedColumnElements: React.ReactNode = null

  if (!isTreeMode && collapsedRow) {
    const firstRenderIndex = groupedColumns.findIndex((_, i) =>
      collapsedRow.groupsToRender.includes(collapsedRow.groupKeys[i])
    )
    groupedColumnElements = groupedColumns.map((vColumn, index) => {
      const groupKey = collapsedRow.groupKeys[index]
      const value = getRowValue(
        collapsedRow.row,
        vColumn.column.publicId,
        spreadsheetData.columnIdToIndex,
        spreadsheetData.columnStore
      )
      const shouldShowValue = collapsedRow.groupsToRender.includes(groupKey)
      const isWithinCollapsedGroup = firstRenderIndex !== -1 && index >= firstRenderIndex

      // Calculate span height from pre-computed group row spans
      const rowSpanCount =
        shouldShowValue && groupRowSpans && rowIndex !== undefined
          ? (groupRowSpans.get(`${rowIndex}-${index}`) ?? 1)
          : 1

      return (
        <GroupCell
          key={vColumn.column.publicId}
          column={vColumn}
          rowHeight={rowHeights.Small}
          colourSettings={colourSettings}
          onGroupToggle={onGroupToggle}
          groupKey={collapsedRow.groupKey}
          value={value}
          canCollapse={false}
          isWithinCollapsedGroup={isWithinCollapsedGroup}
          onContext={onGroupContext}
          shouldShowValue={shouldShowValue}
          columnValuesCount={columnValuesCount}
          rowSpanCount={rowSpanCount}
          row={collapsedRow.row}
          groupItemKey={groupKey}
          inSelectedColumn={selectedColumnIds.includes(vColumn.column.publicId)}
          isFirstRow={isFirstRow}
          selected={selectedGroupCell?.columnId === vColumn.column.publicId && selectedGroupCell?.groupKey === groupKey}
          onSelect={selectGroupCell}
        />
      )
    })
  }

  // Aggregate rows: use treeGroupRow.childRows in tree mode, otherwise the rows prop
  const aggregateRows = isTreeMode && treeGroupRow ? treeGroupRow.childRows : rows

  return (
    <div
      className="absolute top-0 inline-flex m-0 p-0 h-auto"
      style={{
        contain: 'style',
        width: 'var(--grid-width)',
        top: `calc(var(--header-height) + ${rowNumber} * var(--row-height) + var(--row-top-offset) - ${addRowOffset}px)`
      }}
    >
      <RowNumberCell
        rowHeight={rowHeights.Small}
        useGreyBackground={true}
        width={ROW_NUMBER_COLUMN_WIDTH}
        left={0}
        noTopLine={isFirstRow}
      />
      {isTreeMode ? treeCell : groupedColumnElements}

      {nonGroupedColumns.map((column: IViewPortColumn) => {
        return (
          <Aggregate
            key={column.column.publicId}
            column={column}
            cumulativeWidth={column.left}
            width={column.column.width}
            frozen={column.isFrozen}
            isLastFrozenColumn={column.isLastFrozenColumn}
            className={
              isTreeMode ? `${topLine}${treeRowBgColour ? '' : 'bg-surface-sunken'}`.trim() : `${topLine}bg-surface`
            }
            style={
              isTreeMode
                ? {
                    ...(treeRowBgColour ? { backgroundColor: themed.fill(treeRowBgColour) } : {}),
                    color: themed.text(treeRowFontColour, treeRowBgColour),
                    fontWeight: treeRowFontBold ? 'bold' : undefined,
                    fontStyle: treeRowFontItalic ? 'italic' : undefined,
                    textDecoration: treeRowFontUnderline ? 'underline' : undefined
                  }
                : undefined
            }
            height={rowHeightPx}
            rows={aggregateRows}
            loading={loading}
          />
        )
      })}

      {spreadsheetData.isAdmin && (
        <div
          className="ml-auto bg-surface-sunken"
          style={{ width: `${COLUMN_MIN_WIDTH}px`, height: `${rowHeightPx}px` }}
        />
      )}
    </div>
  )
}

export default CollapsedRow
